import { IUsersInfo, UserStatusEnum } from '@monetize/types/app';
import {
  MAvatar,
  MFlex,
  MTag,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { createColumnHelper } from '../../../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  getColHeaderFilters,
  getColHeaderWithSize,
} from '../../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { USER_STATUS_DISPLAY_STRING } from '../../../../../constants/users';

const columnHelper = createColumnHelper<IUsersInfo>();

export const OWNER_ROLE_LABEL = 'Owner';
export const MEMBER_ROLE_LABEL = 'Member';

const ROLE_FILTER_VALUES = [
  { label: OWNER_ROLE_LABEL, value: OWNER_ROLE_LABEL },
  { label: MEMBER_ROLE_LABEL, value: MEMBER_ROLE_LABEL },
];

export const getRoleLabel = ({ id }: IUsersInfo, ownerId: string) =>
  id === ownerId ? OWNER_ROLE_LABEL : MEMBER_ROLE_LABEL;

export interface TeamMembersColumnContext extends ColumnContextBase<IUsersInfo> {
  ownerId: string;
}

/**
 * Members table on the team detail page. The table is `clientControlled`, so
 * header filters and sorting both run in the browser over the full member
 * list. Rows arrive pre-sorted owner-first via `sortTeamUsers`; that is the
 * default order, and it gives way once a column is sorted explicitly.
 */
export const TEAM_MEMBERS_CATALOG: ColumnCatalog<
  IUsersInfo,
  TeamMembersColumnContext
> = [
  {
    id: 'name',
    label: 'Member',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Member', 260),
        // SET rather than TEXT: the filter values are derived from the data,
        // so the checklist is exactly this team's members - no free-text
        // guessing, and no picking someone who isn't on the team.
        ...getColHeaderFilters('SET'),
        cell: ({ getValue }) => (
          <MFlex alignItems="center" gap={2} minW={0}>
            <MAvatar name={getValue()} size="xs" />
            <MTooltip label={getValue()} placement="bottom-start">
              <MText truncate>{getValue()}</MText>
            </MTooltip>
          </MFlex>
        ),
      }),
  },
  {
    id: 'email',
    label: 'Email',
    build: () =>
      columnHelper.accessor('email', {
        ...getColHeaderWithSize('Email', 280),
        ...getColHeaderFilters('SET'),
        // Not `textBodyTemplate`: it wraps on word boundaries, so an address
        // breaks at its `+` or `-` and leaves most of the column empty.
        // `truncate` fills the width and ellipsizes mid-word.
        cell: ({ getValue }) => (
          <MTooltip label={getValue()} placement="bottom-start">
            <MText truncate>{getValue()}</MText>
          </MTooltip>
        ),
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 120),
        // Labels come from `filterDataDisplayMap` on the table, so the
        // checklist reads "Active" rather than the raw enum.
        ...getColHeaderFilters('SET'),
        cell: ({ getValue }) => {
          const status = getValue();
          const label = status && USER_STATUS_DISPLAY_STRING[status];
          if (!label) {
            return <MText color="fg.muted">&mdash;</MText>;
          }
          return (
            <MTag
              size="sm"
              variant={status === UserStatusEnum.ACTIVE ? 'green' : 'orange'}
            >
              {label}
            </MTag>
          );
        },
      }),
  },
  {
    id: 'role',
    label: 'Role',
    build: ({ ownerId }) =>
      // Accessor rather than display column: the derived label is what the
      // SET filter matches on and what sorting compares.
      columnHelper.accessor((member) => getRoleLabel(member, ownerId), {
        id: 'role',
        ...getColHeaderWithSize('Role', 110),
        // The role is derived, not a field on the row, so the checklist
        // values cannot be auto-extracted from the data.
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: ROLE_FILTER_VALUES,
        }),
        cell: ({ getValue }) =>
          getValue() === OWNER_ROLE_LABEL ? (
            <MTag size="sm" variant="blue">
              Owner
            </MTag>
          ) : (
            <MText color="fg.muted">Member</MText>
          ),
        // Ascending puts the owner first, matching the default order.
        sortFn: (rowA, rowB, columnId) =>
          Number(rowB.getValue(columnId) === OWNER_ROLE_LABEL) -
          Number(rowA.getValue(columnId) === OWNER_ROLE_LABEL),
      }),
  },
  actionCatalogColumn<IUsersInfo>(),
];
